<script>
    import { onMount, tick } from "svelte";
    import { push } from "svelte-spa-router";
    import Header from "../components/Header.svelte";
    import * as api from "../utiles/api";
    import { currentGoBack, accountInfo, inspectFilter, initialInspectFilter } from "../stores/store";
    import { get } from "svelte/store";

    let fullDataList = []; // 전체 조회 데이터
    let visibleList = [];  // 화면에 표시할 데이터
    let pageSize = 30;     // 1회당 표시할 데이터 개수 (30개)
    let displayedCount = 30; // 현재 표시 개수
    let isLoading = false; // 로딩 상태 스위치
    let scrollContainer;
    let savedScrollTop = 0; // 주내역 목록 스크롤 위치 보존용

    // 백엔드 파라미터 조건 (검사관리 주내역 조회용)
    let as_bplccode = "";
    let as_wrkshp_code = "2023020001"; // 작업장 (기본값: 2023020001 - 오븐LINE)
    let wrkshpOptions = [];            // 작업장 목록
    let badnOptions = [];              // 불량내역 옵션 목록
    let managtOptions = [];            // 조치사항 옵션 목록
    let ad_plan_sde = "";    // 생산계획 시작일
    let ad_plan_ede = "";    // 생산계획 종료일
    let is_clos_at = true;   // 마감포함 여부 (기본 true -> '%', 체크 해제 시 'N')
    let as_qltinsp_se = "A"; // 검사구분 ('A', 'B', 'C')

    onMount(async () => {
        currentGoBack.set(goBack);

        const acc = get(accountInfo);
        if (acc) {
            as_bplccode = acc.BPLC_CODE || acc.bplc_code || acc.as_bplccode || acc.gs_bplccode || "01";
        }

        await loadWrkshpList();
        await loadReferenceCodes();

        const filterState = get(inspectFilter);
        // 상세내역(InspectDtl)을 다녀왔을 때만 이전 조회조건, 스크롤, 리스트 데이터 복원
        if (filterState && filterState.isFromDetail === true && filterState.fullDataList && filterState.fullDataList.length > 0) {
            as_wrkshp_code = filterState.as_wrkshp_code || "2023020001";
            ad_plan_sde = filterState.ad_plan_sde || "";
            ad_plan_ede = filterState.ad_plan_ede || "";
            is_clos_at = filterState.is_clos_at ?? true;
            as_qltinsp_se = filterState.as_qltinsp_se || "A";
            fullDataList = filterState.fullDataList;
            displayedCount = filterState.displayedCount || 30;
            visibleList = filterState.visibleList || fullDataList.slice(0, displayedCount);

            // 다음번 메인/홈 이동 진입에 대비하여 isFromDetail 플래그 해제
            inspectFilter.update(f => ({ ...f, isFromDetail: false }));

            await tick();
            if (scrollContainer && filterState.scrollTop) {
                scrollContainer.scrollTop = filterState.scrollTop;
            }
        } else {
            // 기본 생산계획일: 오늘 기준 6개월 전 ~ 오늘
            const today = new Date();
            const sixMonthsAgo = new Date();
            sixMonthsAgo.setMonth(today.getMonth() - 6);

            const sde = sixMonthsAgo.toISOString().split("T")[0];
            const ede = today.toISOString().split("T")[0];

            as_wrkshp_code = "2023020001";
            ad_plan_sde = sde;
            ad_plan_ede = ede;
            is_clos_at = true;
            as_qltinsp_se = "A";

            // 메인(Home) 메뉴 등에서 진입 시 조회조건 및 스크롤 위치 초기화
            inspectFilter.set({
                ...initialInspectFilter,
                as_wrkshp_code: "2023020001",
                ad_plan_sde: sde,
                ad_plan_ede: ede,
                isSummaryExpanded: filterState ? filterState.isSummaryExpanded : false
            });

            // 최초 진입 시 기본값(6개월 전~오늘)으로 즉시 자동 조회
            await getData();
        }
    });

    function goBack() {
        push("/Home");
    }

    // 상세내역 페이지로 이동 (카드 상단 [상세내역 >] 버튼 클릭 시)
    function openDetailView(item) {
        if (scrollContainer) {
            savedScrollTop = scrollContainer.scrollTop;
        }

        inspectFilter.update(f => ({
            ...f,
            as_wrkshp_code,
            ad_plan_sde,
            ad_plan_ede,
            is_clos_at,
            as_qltinsp_se,
            fullDataList,
            visibleList,
            displayedCount,
            scrollTop: savedScrollTop,
            selectedMainItem: item,
            isFromDetail: true
        }));

        push(`/InspectDtl/${item.BPLC_CODE}/${item.PLAN_NO}/${item.PLAN_SN}/${item.QLTINSP_SE}`);
    }

    // (L1) ~ (L5) 명칭 포맷
    function formatLevels(item) {
        if (!item) return "";
        const parts = [];
        if (item.LVL1_NM) parts.push(`(L1) ${item.LVL1_NM}`);
        if (item.LVL2_NM) parts.push(`(L2) ${item.LVL2_NM}`);
        if (item.LVL3_NM) parts.push(`(L3) ${item.LVL3_NM}`);
        if (item.LVL4_NM) parts.push(`(L4) ${item.LVL4_NM}`);
        if (item.LVL5_NM) parts.push(`(L5) ${item.LVL5_NM}`);
        return parts.length > 0 ? parts.join(" > ") : "-";
    }

    // 작업장 목록 호출
    async function loadWrkshpList() {
        try {
            const acc = get(accountInfo);
            const userBplc = acc ? (acc.BPLC_CODE || acc.bplc_code || acc.as_bplccode || acc.gs_bplccode) : null;
            const targetBplc = (as_bplccode && as_bplccode !== "%") ? as_bplccode : (userBplc || "01");
            const bplcParam = `as_bplccode=${targetBplc}&gs_bplccode=${targetBplc}&bplc_code=${targetBplc}`;

            const res = await api.get(`/inspect/wrkshp?${bplcParam}`);
            if (res?.data?.data?.length > 0) {
                wrkshpOptions = res.data.data;
            } else if (Array.isArray(res?.data) && res.data.length > 0) {
                wrkshpOptions = res.data;
            } else {
                wrkshpOptions = [{ WRKSHP_CODE: "2023020001", WRKSHP_NM: "오븐LINE" }];
            }
        } catch (err) {
            console.error("작업장 목록 로드 오류:", err);
            wrkshpOptions = [{ WRKSHP_CODE: "2023020001", WRKSHP_NM: "오븐LINE" }];
        }
    }

    // 참조 코드 목록 호출 (QCM_BADN_TY, QCM_MANAGT_TY)
    async function loadReferenceCodes() {
        try {
            const acc = get(accountInfo);
            const userBplc = acc ? (acc.BPLC_CODE || acc.bplc_code || acc.as_bplccode || acc.gs_bplccode) : null;
            const targetBplc = (as_bplccode && as_bplccode !== "%") ? as_bplccode : (userBplc || "01");
            const bplcParam = `&gs_bplccode=${targetBplc}&as_bplccode=${targetBplc}&bplc_code=${targetBplc}`;

            // 1. 불량내역 (QCM_BADN_TY)
            const badnRes = await api.get(`/inspect/refrn?cl_code=QCM_BADN_TY${bplcParam}`);
            if (badnRes?.data?.data?.length > 0) {
                let list = [...badnRes.data.data];
                badnOptions = list;
            } else if (Array.isArray(badnRes?.data)) {
                badnOptions = [...badnRes.data];
            }

            // 2. 조치사항 (QCM_MANAGT_TY)
            const managtRes = await api.get(`/inspect/refrn?cl_code=QCM_MANAGT_TY${bplcParam}`);
            if (managtRes?.data?.data?.length > 0) {
                let list = [...managtRes.data.data];
                managtOptions = list;
            } else if (Array.isArray(managtRes?.data)) {
                managtOptions = [...managtRes.data];
            }
        } catch (err) {
            // [디버그 로그] 로직과 무관, 에러 확인용 콘솔 출력 - 필요시 이 라인만 찾아 수정/제거
            console.error("참조 코드 로드 오류:", err);
        }
    }

    // 백엔드 API 데이터 호출 (검사관리 주내역)
    async function getData() {
        isLoading = true;
        const startTime = Date.now();

        try {
            const acc = get(accountInfo);
            const userBplc = acc ? (acc.BPLC_CODE || acc.bplc_code || acc.as_bplccode || acc.gs_bplccode) : null;
            const targetBplc = (as_bplccode && as_bplccode !== "%") ? as_bplccode : (userBplc || "01");

            const queryParams = new URLSearchParams();
            if (as_wrkshp_code && as_wrkshp_code !== "%") queryParams.append("as_wrkshp_code", as_wrkshp_code);
            if (ad_plan_sde) queryParams.append("ad_plan_sde", ad_plan_sde.replace(/-/g, ""));
            if (ad_plan_ede) queryParams.append("ad_plan_ede", ad_plan_ede.replace(/-/g, ""));
            if (!is_clos_at) queryParams.append("as_clos_at", "N");
            if (as_qltinsp_se && as_qltinsp_se !== "%") queryParams.append("as_qltinsp_se", as_qltinsp_se);
            if (targetBplc && targetBplc !== "%") {
                queryParams.append("as_bplccode", targetBplc);
                queryParams.append("gs_bplccode", targetBplc);
            }

            // [디버그 로그] 로직과 무관, 요청/응답 확인용 콘솔 출력 - 필요시 이 블록만 찾아 수정/제거
            console.log("[InspectList FRONT] ===== 주내역 API 요청 준비 =====");
            console.log("[InspectList FRONT] 요청 URL:", `/inspect?${queryParams.toString()}`);

            const response = await api.get(`/inspect?${queryParams.toString()}`);
            console.log("[InspectList FRONT] API 수신 전체 응답:", response);

            const listData = response && response.data && response.data.data ? response.data.data : (response && response.data ? response.data : null);
            if (listData && Array.isArray(listData)) {
                console.log("[InspectList FRONT] 수신된 주내역 DB 레코드 건수:", listData.length);
                fullDataList = listData.map(rawItem => {
                    const item = {};
                    if (rawItem && typeof rawItem === 'object') {
                        for (const k of Object.keys(rawItem)) {
                            item[k] = rawItem[k];
                            item[k.toUpperCase()] = rawItem[k];
                            item[k.toLowerCase()] = rawItem[k];
                        }
                    }
                    return item;
                });
            } else {
                // [디버그 로그] 로직과 무관, 응답 이상 확인용 콘솔 출력 - 필요시 이 라인만 찾아 수정/제거
                console.warn("[InspectList FRONT] 수신 데이터가 없거나 배열이 아닙니다:", response);
                fullDataList = [];
            }
        } catch (err) {
            // [디버그 로그] 로직과 무관, 에러 확인용 콘솔 출력 - 필요시 이 라인만 찾아 수정/제거
            console.error("[InspectList FRONT] API 호출 에러 발생:", err);
            fullDataList = [];
        } finally {
            const elapsedTime = Date.now() - startTime;
            const remainingTime = Math.max(0, 300 - elapsedTime);
            
            setTimeout(() => {
                isLoading = false;
                displayedCount = pageSize;
                visibleList = fullDataList.slice(0, displayedCount);
            }, remainingTime);
        }
    }

    let showScrollTop = false;

    function handleScroll() {
        if (!scrollContainer) return;
        const { scrollTop, scrollHeight, clientHeight } = scrollContainer;

        showScrollTop = scrollTop > 150;

        if (!isLoading && scrollTop + clientHeight >= scrollHeight - 250) {
            if (displayedCount < fullDataList.length) {
                displayedCount += pageSize;
                visibleList = fullDataList.slice(0, displayedCount);
            }
        }
    }

    function scrollToTop() {
        if (scrollContainer) {
            scrollContainer.scrollTop = 0;
        }
    }

    function saveData() {
        alert("저장 기능이 실행되었습니다.");
    }

    function exitPage() {
        if (confirm("화면을 종료하고 메인 페이지로 이동하시겠습니까?")) {
            goBack();
        }
    }

    // --- MES 사원 조회 모달 제어 ---
    function openEmpModal(item) {
        selectedItemForEmp = item;
        modalSearchEmpno = "";
        modalSearchKoreanNm = "";
        modalSearchDept = "";
        modalSearchHffc = "";
        
        if (item && item.OPERTOR_EMPNO) {
            selectedEmpRow = { EMPNO: item.OPERTOR_EMPNO, KOREAN_NM: item.OPERTOR_EMPNO_NM || "" };
        } else {
            selectedEmpRow = null;
        }

        isEmpModalOpen = true;
        fetchEmpList();
    }

    async function fetchEmpList() {
        isModalLoading = true;
        try {
            const queryParams = new URLSearchParams();
            if (modalSearchEmpno) queryParams.append("as_empno", modalSearchEmpno);
            if (modalSearchKoreanNm) queryParams.append("as_korean_nm", modalSearchKoreanNm);
            if (modalSearchDept) queryParams.append("as_dept_nm", modalSearchDept);
            if (modalSearchHffc) queryParams.append("as_hffc", modalSearchHffc);

            const response = await api.get(`/inspect/emp?${queryParams.toString()}`);
            if (response && response.data && response.data.data && response.data.data.length > 0) {
                empList = response.data.data;
            } else {
                generateSampleEmpList();
            }
        } catch (err) {
            // [디버그 로그] 로직과 무관, 에러 확인용 콘솔 출력 - 필요시 이 라인만 찾아 수정/제거
            console.error("사원 데이터 조회 오류, 샘플 생성:", err);
            generateSampleEmpList();
        } finally {
            isModalLoading = false;
        }
    }

    function generateSampleEmpList() {
        empList = [
            { EMPNO: "20260713", KOREAN_NM: "고유리", DEPT_NM: "품질관리팀", OFCPS_CODE: "03", OFCPS_NM: "대리", HFFC: "1" },
            { EMPNO: "2026071301", KOREAN_NM: "김수범", DEPT_NM: "생산1팀", OFCPS_CODE: "01", OFCPS_NM: "사원", HFFC: "1" },
            { EMPNO: "2026071302", KOREAN_NM: "이상희", DEPT_NM: "생산2팀", OFCPS_CODE: "02", OFCPS_NM: "주임", HFFC: "2" },
            { EMPNO: "20260715", KOREAN_NM: "김태윤", DEPT_NM: "생산관리팀", OFCPS_CODE: "04", OFCPS_NM: "과장", HFFC: "1" },
            { EMPNO: "EMP001", KOREAN_NM: "홍길동", DEPT_NM: "제조1팀", OFCPS_CODE: "05", OFCPS_NM: "팀장", HFFC: "1" },
            { EMPNO: "EMP002", KOREAN_NM: "이순신", DEPT_NM: "검사팀", OFCPS_CODE: "06", OFCPS_NM: "부장", HFFC: "2" }
        ];

        empList = empList.filter((e) => {
            const matchNo = !modalSearchEmpno || e.EMPNO.includes(modalSearchEmpno);
            const matchNm = !modalSearchKoreanNm || e.KOREAN_NM.includes(modalSearchKoreanNm);
            const matchDept = !modalSearchDept || (e.DEPT_NM && e.DEPT_NM.includes(modalSearchDept));
            const matchHffc = !modalSearchHffc || e.HFFC === modalSearchHffc;
            return matchNo && matchNm && matchDept && matchHffc;
        });
    }

    function handleCardClick(emp) {
        const currentTime = Date.now();
        const timeDiff = currentTime - lastTapTime;

        if (selectedEmpRow && selectedEmpRow.EMPNO === emp.EMPNO) {
            selectedEmpRow = null;
        } else {
            selectedEmpRow = emp;
            if (timeDiff < 380 && timeDiff > 0) {
                confirmSelectEmp(emp);
                return;
            }
        }
        lastTapTime = currentTime;
    }

    function confirmSelectEmp(emp) {
        const targetEmp = emp !== undefined ? emp : selectedEmpRow;
        if (selectedItemForEmp) {
            if (targetEmp) {
                selectedItemForEmp.OPERTOR_EMPNO = targetEmp.EMPNO;
                selectedItemForEmp.OPERTOR_EMPNO_NM = targetEmp.KOREAN_NM;
            } else {
                selectedItemForEmp.OPERTOR_EMPNO = "";
                selectedItemForEmp.OPERTOR_EMPNO_NM = "";
            }
            visibleList = [...visibleList];
        }
        closeEmpModal();
    }

    function resetEmpModalForm() {
        modalSearchEmpno = "";
        modalSearchKoreanNm = "";
        modalSearchDept = "";
        modalSearchHffc = "";
        selectedEmpRow = null;
        lastTapTime = 0;
        
        fetchEmpList();
    }

    function closeEmpModal() {
        isEmpModalOpen = false;
        selectedEmpRow = null;
        lastTapTime = 0;
    }
</script>

<Header {goBack} />

<!-- 전체 화면 회색 반투명 로딩 오버레이 -->
{#if isLoading}
    <div class="full-screen-loading-overlay">
        <div class="loading-box">
            <div class="loading-spinner"></div>
            <p class="loading-title">데이터를 불러오는 중입니다...</p>
            <p class="loading-sub">잠시만 기다려 주세요.</p>
        </div>
    </div>
{/if}

<!-- svelte-ignore a11y-click-events-have-key-events -->
<section
    bind:this={scrollContainer}
    class="main inner main-pad-36 main-gray scroll-custom"
    style="width: 100%; position: relative; height: calc(100vh - 60px); overflow-y: auto;"
    on:scroll={handleScroll}
>
    <div class="mobile">
        <!-- 상단 헤더 & 제목 -->
            <div style="background: #FFF; border-radius: 12px; box-shadow: 0 2px 8px rgba(0,0,0,0.06); padding: 16px;">
                <div class="cmn-title-left" style="display: flex; justify-content: space-between; align-items: center;">
                    <p style="font-size: 20px; font-weight: 700; color: #2D3C48;">검사항목 주 내역</p>
                    <span class="total-count-tag">총 {fullDataList.length}건</span>
                </div>

                <!-- 상단 검색 필터 영역 -->
                <div class="search-filter-card">
                    <!-- 1행: 생산계획일 (시작일 ~ 종료일) -->
                    <div class="filter-row">
                        <label class="filter-label" for="ad_plan_sde">생산계획일</label>
                        <div class="date-range-group">
                            <input id="ad_plan_sde" type="date" bind:value={ad_plan_sde} class="search-input-date" />
                            <span class="date-sep">~</span>
                            <input type="date" bind:value={ad_plan_ede} class="search-input-date" />
                        </div>
                    </div>

                    <!-- 2행: 작업장 & 검사구분 -->
                    <div class="filter-row col-2">
                        <div class="filter-group">
                            <label class="filter-label" for="as_wrkshp_code">작업장</label>
                            <select id="as_wrkshp_code" bind:value={as_wrkshp_code} class="search-select">
                                {#each wrkshpOptions as option}
                                    <option value={option.WRKSHP_CODE || option.wrkshp_code || option.WRKSHP_CODE}>
                                        {option.WRKSHP_NM || option.wrkshp_nm || option.WRKSHP_NM}
                                    </option>
                                {/each}
                            </select>
                        </div>
                        <div class="filter-group">
                            <label class="filter-label" for="as_qltinsp_se">검사구분</label>
                            <select id="as_qltinsp_se" bind:value={as_qltinsp_se} class="search-select">
                                <option value="A">시운전검사</option>
                                <option value="B">출하검사</option>
                                <option value="C">장기 시운전</option>
                            </select>
                        </div>
                    </div>

                    <!-- 3행: 마감포함 체크박스 & 조회 버튼 -->
                    <div class="filter-row end-row">
                        <label class="clos-check-label" class:active={is_clos_at}>
                            <input type="checkbox" bind:checked={is_clos_at} style="display: none;" />
                            <span class="custom-checkbox-box">
                                {#if is_clos_at}✓{/if}
                            </span>
                            <span class="clos-text">마감포함</span>
                        </label>

                        <button class="action-btn search-btn inline-btn" on:click={getData}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            조회
                        </button>
                    </div>
                </div>
            </div>

            <!-- 검사항목 주 내역 카드 리스트 -->
            <div class="inspect-card-list" style="margin-top: 16px;">
                {#if visibleList.length === 0}
                    <div style="background: #FFF; border-radius: 12px; padding: 40px 20px; text-align: center; color: #94A3B8; font-size: 14px; box-shadow: 0 2px 8px rgba(0,0,0,0.04);">
                        조회된 검사항목 주 내역 데이터가 없습니다.
                    </div>
                {:else}
                    {#each visibleList as item, index (item.PLAN_NO + '_' + item.PLAN_SN + '_' + index)}
                    <div class="inspect-card">
                        <!-- 카드 헤더 (No.1 옆에 생산계획번호 배치, 오른쪽 상세내역 버튼) -->
                        <div class="inspect-card-header">
                            <div class="inspect-header-left">
                                <span class="sn-badge">No.{index + 1}</span>
                                <span class="plan-no-text">{item.PLAN_NO || '-'}</span>
                            </div>
                            <button class="detail-link-btn" on:click={() => openDetailView(item)}>
                                상세내역 <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 18l6-6-6-6"/></svg>
                            </button>
                        </div>

                        <!-- 카드 바디 (50% 50% 반반 분할, 각각 중앙 정렬) -->
                        <div class="inspect-card-body">
                            <div class="card-multi-row col-2-center">
                                <div class="info-cell cell-center">
                                    <span class="cell-label">품목코드</span>
                                    <span class="cell-val accent">{item.ITEM_CODE || '-'}</span>
                                </div>
                                <div class="info-cell cell-center">
                                    <span class="cell-label">품목명</span>
                                    <span class="cell-val bold">{item.ITEM_NM || '-'}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                {/each}
                {/if}

                {#if visibleList.length < fullDataList.length}
                    <div class="loading-more">
                        <p>스크롤 시 30개씩 더 보기 ({visibleList.length} / {fullDataList.length})</p>
                    </div>
                {/if}
            </div>
    </div>

    <!-- 우측 하단 플로팅 맨위로 버튼 -->
    {#if showScrollTop}
        <button class="scroll-top-btn" on:click={scrollToTop} aria-label="맨위로 이동">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="18 15 12 9 6 15"/>
            </svg>
            <span>TOP</span>
        </button>
    {/if}
</section>

<style>
    /* MES 모바일 대칭 정중앙 세로 90% 높이 차지 팝업 모달 */
    .mes-modal-overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        background: rgba(15, 23, 42, 0.65);
        backdrop-filter: blur(4px);
        z-index: 99999;
        display: flex;
        justify-content: center;
        align-items: center;
        padding: 12px;
        box-sizing: border-box;
    }

    .mes-modal-window {
        background: #F8FAFC;
        border: 1px solid #CBD5E1;
        width: 100%;
        max-width: 360px;
        height: 90vh;
        max-height: 92vh;
        border-radius: 14px;
        box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
        display: flex;
        flex-direction: column;
        overflow: hidden;
        margin: 0 auto;
        box-sizing: border-box;
    }

    /* 1. 상단 툴바 버튼 영역 ([조회] [선택] [취소] [닫기]) */
    .mes-toolbar {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        gap: 6px;
        padding: 10px;
        background: #1E293B;
        border-bottom: 1px solid #334155;
        box-sizing: border-box;
    }

    .mes-tb-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 3px;
        height: 34px;
        padding: 0 4px;
        border-radius: 6px;
        border: none;
        background: #334155;
        font-size: 12px;
        font-weight: 700;
        color: #FFFFFF;
        cursor: pointer;
        transition: all 0.15s ease;
        white-space: nowrap;
    }
    .mes-tb-btn:hover {
        background: #475569;
    }
    .mes-tb-btn.search {
        background: #2563EB;
    }
    .mes-tb-btn.select {
        background: #059669;
    }
    .mes-tb-btn.cancel {
        background: #DC2626;
    }

    .tb-icon {
        font-size: 11px;
    }

    /* 2. 1열 4줄 검색 조건 필터 바 (사원번호, 사원명, 부서, 재직구분) */
    .mes-filter-bar.vertical-4row {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 12px 14px;
        background: #FFFFFF;
        border-bottom: 1px solid #E2E8F0;
        box-sizing: border-box;
    }

    .filter-item {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .filter-item label {
        font-size: 13px;
        font-weight: 700;
        color: #334155;
        width: 60px;
        flex-shrink: 0;
    }

    .mes-input, .mes-select {
        flex: 1;
        height: 34px;
        padding: 0 10px;
        border: 1px solid #CBD5E1;
        border-radius: 6px;
        font-size: 13px;
        background: #FFFFFF;
        color: #1E293B;
        box-sizing: border-box;
    }

    /* 3. 카드형 사원 리스트 (90% 세로 높이 내부에서 flex: 1) */
    .emp-card-list {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 12px;
        flex: 1;
        overflow-y: auto;
        background: #F1F5F9;
        box-sizing: border-box;
    }

    .emp-card-item {
        position: relative;
        background: #FFFFFF;
        border: 1px solid #E2E8F0;
        border-radius: 12px;
        padding: 14px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        cursor: pointer;
        transition: all 0.15s ease;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);
        box-sizing: border-box;
    }
    .emp-card-item:hover {
        border-color: #93C5FD;
        background: #F8FAFC;
    }
    .emp-card-item.selected {
        background: #EFF6FF;
        border: 2px solid #2563EB;
        box-shadow: 0 4px 14px rgba(37, 99, 235, 0.18);
    }

    .card-checked-badge {
        position: absolute;
        top: 10px;
        right: 12px;
        font-size: 11px;
        font-weight: 700;
        color: #2563EB;
        background: #DBEAFE;
        border: 1px solid #93C5FD;
        padding: 2px 8px;
        border-radius: 10px;
    }

    /* 2x2 균일 그리드 레이아웃 (모든 항목 라벨 상단, 내용 하단 고정) */
    .emp-card-grid-4 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 12px 14px;
    }

    .info-cell {
        display: flex;
        flex-direction: column;
        gap: 3px;
        text-align: left;
    }
    .cell-label {
        font-size: 13px;
        color: #64748B;
        font-weight: 600;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
        letter-spacing: -0.1px;
    }
    .cell-val {
        font-size: 15px;
        color: #4A4A4A;
        font-weight: 600;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
        letter-spacing: -0.1px;
    }
    .cell-val.bold {
        font-size: 16px;
        font-weight: 700;
        color: #0F172A;
    }
    .cell-val.accent {
        font-size: 15px;
        font-weight: 600;
        color: #1E293B;
    }

    .empty-state {
        text-align: center;
        padding: 40px 0;
        font-size: 13px;
        color: #94A3B8;
    }

    /* 4. 하단 상태바 */
    .mes-statusbar {
        padding: 8px 10px;
        background: #E2E8F0;
        border-top: 1px solid #CBD5E1;
        font-size: 11px;
        color: #475569;
        text-align: center;
        box-sizing: border-box;
    }

    /* 전체 화면 회색 반투명 로딩 오버레이 */
    .full-screen-loading-overlay {
        position: fixed;
        top: 0;
        left: 0;
        width: 100vw;
        height: 100vh;
        background: rgba(45, 60, 72, 0.65);
        backdrop-filter: blur(4px);
        z-index: 999999;
        display: flex;
        justify-content: center;
        align-items: center;
    }

    .loading-box {
        background: #FFFFFF;
        padding: 28px 36px;
        border-radius: 16px;
        box-shadow: 0 12px 32px rgba(0, 0, 0, 0.25);
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        min-width: 260px;
    }

    .loading-spinner {
        width: 44px;
        height: 44px;
        border: 4px solid #E2E8F0;
        border-top: 4px solid #2563EB;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
        margin-bottom: 16px;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    .loading-title {
        font-size: 15px;
        font-weight: 700;
        color: #1E293B;
        margin: 0 0 6px 0;
    }
    .loading-sub {
        font-size: 12px;
        color: #64748B;
        margin: 0;
    }

    .total-count-tag {
        font-size: 12px;
        font-weight: 600;
        color: #1E293B;
        background: #F1F5F9;
        padding: 4px 10px;
        border-radius: 12px;
    }

    .action-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 6px;
        height: 42px;
        border-radius: 8px;
        border: none;
        font-size: 14px;
        font-weight: 700;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
    }
    .action-btn:active {
        transform: scale(0.97);
    }
    .action-btn.search-btn {
        background: #2563EB;
        color: #FFFFFF;
    }
    .action-btn.search-btn:hover {
        background: #1D4ED8;
    }

    /* 플로팅 맨위로 버튼 */
    .scroll-top-btn {
        position: fixed;
        bottom: 24px;
        right: 20px;
        z-index: 99999;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background: #1E293B;
        color: #FFFFFF;
        border: 2px solid #334155;
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
        cursor: pointer;
        transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    }
    .scroll-top-btn span {
        font-size: 10px;
        font-weight: 800;
        margin-top: -2px;
        letter-spacing: 0.5px;
    }
    .scroll-top-btn:hover {
        background: #2563EB;
        border-color: #60A5FA;
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(37, 99, 235, 0.4);
    }

    .bplc-title {
        font-size: 16px;
        font-weight: 700;
        color: #1E293B;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
    }

    /* 상단 검사항목 주내역 검색 필터 카드 */
    .search-filter-card {
        background: #F8FAFC;
        border-radius: 10px;
        border: 1px solid #E2E8F0;
        padding: 12px 14px;
        margin-top: 12px;
        display: flex;
        flex-direction: column;
        gap: 10px;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
        box-sizing: border-box;
    }

    .filter-row {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 100%;
        min-width: 0;
    }
    .filter-row.col-2 {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 8px;
        min-width: 0;
    }
    .filter-row.end-row {
        justify-content: space-between;
        margin-top: 2px;
    }

    .filter-group {
        display: flex;
        align-items: center;
        gap: 6px;
        min-width: 0;
        width: 100%;
    }

    .filter-label {
        font-size: 13px;
        font-weight: 600;
        color: #4A4A4A;
        white-space: nowrap;
        flex-shrink: 0;
    }

    .date-range-group {
        display: flex;
        align-items: center;
        gap: 4px;
        flex: 1;
        min-width: 0;
        width: 100%;
    }

    .search-input-date, .search-select {
        flex: 1;
        min-width: 0;
        width: 100%;
        height: 38px;
        padding: 0 4px;
        border-radius: 6px;
        border: 1px solid #CBD5E1;
        font-size: 13px;
        color: #4A4A4A;
        background: #FFFFFF;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
        box-sizing: border-box;
    }

    .date-sep {
        font-size: 13px;
        font-weight: 700;
        color: #64748B;
        flex-shrink: 0;
    }

    .clos-check-label {
        display: inline-flex;
        align-items: center;
        gap: 8px;
        padding: 6px 12px;
        border-radius: 8px;
        background: #F1F5F9;
        border: 1px solid #CBD5E1;
        cursor: pointer;
        user-select: none;
        transition: all 0.2s ease;
    }
    .clos-check-label.active {
        background: #EFF6FF;
        border-color: #2563EB;
    }
    .custom-checkbox-box {
        width: 20px;
        height: 20px;
        border-radius: 4px;
        border: 2px solid #94A3B8;
        background: #FFFFFF;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        font-weight: 800;
        color: #FFFFFF;
        transition: all 0.15s ease;
    }
    .clos-check-label.active .custom-checkbox-box {
        background: #2563EB;
        border-color: #2563EB;
        color: #FFFFFF;
    }
    .clos-text {
        font-size: 14px;
        font-weight: 700;
        color: #475569;
    }
    .clos-check-label.active .clos-text {
        color: #1E4ED8;
    }

    .action-btn.inline-btn {
        height: 38px;
        padding: 0 18px;
        font-size: 14px;
    }

    /* 주내역 멀티행 카드 구조 */
    .card-multi-row {
        display: flex;
        gap: 10px;
        padding: 6px 0;
        border-bottom: 1px dashed #F1F5F9;
    }
    .card-multi-row:last-child {
        border-bottom: none;
    }
    .card-multi-row.col-3 > .info-cell {
        flex: 1;
    }
    .card-multi-row.col-1 > .info-cell {
        flex: 1;
    }
    .card-multi-row.col-2 > .info-cell {
        flex: 1;
    }
    .card-multi-row.col-2 > .info-cell.flex-2 {
        flex: 2;
    }
    .card-multi-row.col-4 > .info-cell {
        flex: 1;
    }

    .inspect-card-list {
        display: flex;
        flex-direction: column;
        gap: 14px;
        padding-bottom: 40px;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
        font-feature-settings: 'clig' off, 'liga' off;
        letter-spacing: -0.1px;
    }

    .inspect-card {
        background: #FFFFFF;
        border-radius: 10px;
        border: 1px solid #E2E8F0;
        box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
        padding: 12px 14px;
        display: flex;
        flex-direction: column;
        gap: 8px;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
        font-feature-settings: 'clig' off, 'liga' off;
        letter-spacing: -0.1px;
    }

    .inspect-card-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding-bottom: 8px;
        border-bottom: 1px solid #F1F5F9;
    }

    .inspect-header-left {
        display: flex;
        align-items: center;
        gap: 8px;
    }
    .sn-badge {
        font-size: 13px;
        font-weight: 700;
        color: #1E293B;
        background: #F1F5F9;
        padding: 3px 8px;
        border-radius: 6px;
    }
    .plan-no-text {
        font-size: 14px;
        font-weight: 700;
        color: #0F172A;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
    }
    .info-cell.text-right {
        text-align: right;
        align-items: flex-end;
    }
    .card-multi-row.col-2-center {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: 10px;
        padding: 6px 0;
    }
    .info-cell.cell-center {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    .detail-link-btn {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        height: 30px;
        padding: 0 12px;
        border-radius: 6px;
        background: #2563EB;
        color: #FFFFFF;
        font-size: 13px;
        font-weight: 700;
        border: none;
        cursor: pointer;
        transition: all 0.2s ease;
        box-shadow: 0 2px 4px rgba(37, 99, 235, 0.2);
    }
    .detail-link-btn:hover {
        background: #1D4ED8;
    }
    .detail-link-btn:active {
        transform: scale(0.96);
    }
    .se-badge {
        font-size: 13px;
        font-weight: 600;
        color: #059669;
        background: #ECFDF5;
        padding: 3px 9px;
        border-radius: 12px;
    }

    .inspect-card-body {
        display: flex;
        flex-direction: column;
        gap: 2px;
    }

    .loading-more {
        text-align: center;
        padding: 16px 0;
        font-size: 14px;
        color: #64748B;
        font-weight: 600;
        font-family: Inter, 'Noto Sans CJK KR', sans-serif;
    }
</style>
